import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Loader2,
  ShieldCheck,
  UserCog,
  Search,
  Save,
  CheckSquare,
  RefreshCw,
  RotateCcw,
  X,
  Plus,
  Store,
  EyeOff,
  Eye,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../Context/GlobalContext";
import useSuperAdmin from "../Services/SuperAdminServices/useSuperAdmin";

const makeAccessKey = (menuId, submenuId) => `${menuId}__${submenuId}`;

const flattenAccess = (menuList = []) => {
  const output = [];

  menuList.forEach((menu) => {
    (menu.submenus || []).forEach((sub) => {
      output.push({
        menu_id: String(menu.pk_menu_id),
        submenu_id: String(sub.pk_submenu_id),
      });
    });
  });

  return output;
};

const AdminAccessManagement = () => {
  const { adminId, AllClients } = useContext(GlobalContext);


  const {
    getAllAdminUsers,
    getSuperAdminUserAllData,
    getDataViaAdminID,
    updateAdminRoleStatus,
    getRoleAdmin,
    updateRoleAdmin,
    updateAllAccess,
    insertAdminLogin,
    insertAdminRestaurantMapping,
    getAdminRestaurantMapping,
  } = useSuperAdmin();

  const [admins, setAdmins] = useState([]);
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);

  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [originalAccess, setOriginalAccess] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");

  const [adminSearch, setAdminSearch] = useState("");
  const [menuSearch, setMenuSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [grantingAll, setGrantingAll] = useState(false);
  const [activeTab, setActiveTab] = useState("access");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [originalRestaurants, setOriginalRestaurants] = useState([]);
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [savingRestaurants, setSavingRestaurants] = useState(false);

  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [newAdmin, setNewAdmin] = useState({
    admin_username: "",
    first_name: "",
    last_name: "",
    admin_loginid: "",
    admin_password: "",
    emp_ID: "",
    billing_name: "",
    role: "",
  });

  const selectedAccessSet = useMemo(() => {
    return new Set(
      selectedAccess.map((x) => makeAccessKey(x.menu_id, x.submenu_id))
    );
  }, [selectedAccess]);

  const originalAccessSet = useMemo(() => {
    return new Set(
      originalAccess.map((x) => makeAccessKey(x.menu_id, x.submenu_id))
    );
  }, [originalAccess]);

  const pendingChanges = useMemo(() => {
    const selectedMap = new Set(
      selectedAccess.map((x) => makeAccessKey(x.menu_id, x.submenu_id))
    );

    const originalMap = new Set(
      originalAccess.map((x) => makeAccessKey(x.menu_id, x.submenu_id))
    );

    const toEnable = selectedAccess.filter(
      (x) => !originalMap.has(makeAccessKey(x.menu_id, x.submenu_id))
    );

    const toDisable = originalAccess.filter(
      (x) => !selectedMap.has(makeAccessKey(x.menu_id, x.submenu_id))
    );

    return {
      toEnable,
      toDisable,
      total: toEnable.length + toDisable.length,
    };
  }, [selectedAccess, originalAccess]);

  const loadInitial = useCallback(async () => {
    if (!adminId) return;

    setLoading(true);

    try {
      const [adminRes, menuRes, roleRes] = await Promise.all([
        getAllAdminUsers(adminId),
        getSuperAdminUserAllData(),
        getRoleAdmin(),
      ]);

      if (adminRes.success) setAdmins(adminRes.data);
      else toast.error(adminRes.message);

      if (menuRes.success) setMenus(menuRes.data);
      else toast.error(menuRes.message);

      if (roleRes.success) setRoles(roleRes.data);
      else toast.error(roleRes.message);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const fetchAssignedAccessForAdmin = async (targetAdminId) => {
    if (!targetAdminId) return;

    setAssignedLoading(true);

    try {
      const result = await getDataViaAdminID({
        admin_id: targetAdminId,
      });

      if (result.success) {
        const given = flattenAccess(result.data);
        setSelectedAccess(given);
        setOriginalAccess(given);
      } else {
        setSelectedAccess([]);
        setOriginalAccess([]);
        toast.error(result.message);
      }
    } finally {
      setAssignedLoading(false);
    }
  };

const fetchAssignedRestaurantsForAdmin = async (targetAdminId) => {
  if (!targetAdminId) return;

  setRestaurantLoading(true);

  try {
    const result = await getAdminRestaurantMapping({
      fk_admin_id: String(targetAdminId),
    });

    console.log("Restaurant mapping response:", result);

    if (result.success) {
      const mappings = Array.isArray(result.data)
        ? result.data
        : [];

      // API response uses pk_user_id
      const restaurantIds = mappings
        .map((item) => item?.pk_user_id)
        .filter(
          (id) => id !== null && id !== undefined && id !== ""
        )
        .map(String);

      console.log("Already assigned restaurant IDs:", restaurantIds);

      setOriginalRestaurants(restaurantIds);
      setSelectedRestaurants(restaurantIds);
    } else {
      setOriginalRestaurants([]);
      setSelectedRestaurants([]);

      toast.error(
        result.message || "Failed to load restaurant mappings."
      );
    }
  } catch (error) {
    console.error("Fetch restaurant mapping error:", error);

    setOriginalRestaurants([]);
    setSelectedRestaurants([]);

    toast.error("Failed to load restaurant mappings.");
  } finally {
    setRestaurantLoading(false);
  }
};

  const handleSelectAdmin = (adm) => {
    setSelectedAdmin(adm);

    // Reset access
    setSelectedAccess([]);
    setOriginalAccess([]);

    // Reset restaurant state while loading
    setSelectedRestaurants([]);
    setOriginalRestaurants([]);

    setSelectedRoleId(
      String(
        adm.fk_role_id ||
        adm.role_id ||
        adm.pk_role_id ||
        adm.roleId ||
        adm?.pkid ||
        ""
      )
    );

    // Existing access API
    fetchAssignedAccessForAdmin(adm.id);

    // New restaurant mapping API
    fetchAssignedRestaurantsForAdmin(adm.id);
  };

  const filteredAdmins = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return admins;

    return admins.filter((adm) => {
      return (
        String(adm.admin_username || "").toLowerCase().includes(q) ||
        String(adm.first_name || "").toLowerCase().includes(q) ||
        String(adm.last_name || "").toLowerCase().includes(q) ||
        String(adm.admin_loginid || "").toLowerCase().includes(q) ||
        String(adm.emp_ID || "").toLowerCase().includes(q)
      );
    });
  }, [admins, adminSearch]);

  const filteredMenus = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return menus;

    return menus.filter((menu) => {
      const menuMatch =
        String(menu.menu_title || "").toLowerCase().includes(q) ||
        String(menu.menu_url || "").toLowerCase().includes(q);

      const submenuMatch = (menu.submenus || []).some((sub) => {
        return (
          String(sub.submenu_title || "").toLowerCase().includes(q) ||
          String(sub.submenu_url || "").toLowerCase().includes(q)
        );
      });

      return menuMatch || submenuMatch;
    });
  }, [menus, menuSearch]);

  const isChecked = (menuId, submenuId) => {
    return selectedAccessSet.has(makeAccessKey(menuId, submenuId));
  };

  const isOriginal = (menuId, submenuId) => {
    return originalAccessSet.has(makeAccessKey(menuId, submenuId));
  };

  const handleToggleSingleAccess = (menuId, submenuId) => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    const key = makeAccessKey(menuId, submenuId);

    setSelectedAccess((prev) => {
      const exists = prev.some(
        (x) => makeAccessKey(x.menu_id, x.submenu_id) === key
      );

      if (exists) {
        return prev.filter(
          (x) => makeAccessKey(x.menu_id, x.submenu_id) !== key
        );
      }

      return [
        ...prev,
        {
          menu_id: String(menuId),
          submenu_id: String(submenuId),
        },
      ];
    });
  };

  const handleToggleWholeMenu = (menu) => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    const submenuList = menu.submenus || [];

    if (!submenuList.length) return;

    const allSelected = submenuList.every((sub) =>
      isChecked(menu.pk_menu_id, sub.pk_submenu_id)
    );

    setSelectedAccess((prev) => {
      let next = [...prev];

      if (allSelected) {
        const removeKeys = new Set(
          submenuList.map((sub) =>
            makeAccessKey(menu.pk_menu_id, sub.pk_submenu_id)
          )
        );

        return next.filter(
          (x) => !removeKeys.has(makeAccessKey(x.menu_id, x.submenu_id))
        );
      }

      const existing = new Set(
        next.map((x) => makeAccessKey(x.menu_id, x.submenu_id))
      );

      submenuList.forEach((sub) => {
        const key = makeAccessKey(menu.pk_menu_id, sub.pk_submenu_id);

        if (!existing.has(key)) {
          next.push({
            menu_id: String(menu.pk_menu_id),
            submenu_id: String(sub.pk_submenu_id),
          });
        }
      });

      return next;
    });
  };

  const handleResetChanges = () => {
    setSelectedAccess(originalAccess);
  };

  const handleSaveAccess = async () => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    const requests = [...pendingChanges.toEnable, ...pendingChanges.toDisable];

    if (requests.length === 0) {
      toast("No access changes found.");
      return;
    }

    setSavingAccess(true);

    try {
      const result = await updateAdminRoleStatus({
        admin_id: selectedAdmin.id,
        requests,
      });

      if (result.success) {
        toast.success(result.message || "Access updated successfully.");
        await fetchAssignedAccessForAdmin(selectedAdmin.id);
      } else {
        toast.error(result.message);
      }
    } finally {
      setSavingAccess(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    if (!selectedRoleId) {
      toast.error("Please select role.");
      return;
    }

    setSavingRole(true);

    try {
      const result = await updateRoleAdmin({
        adminId: selectedAdmin.id,
        roleId: selectedRoleId,
      });

      if (result.success) toast.success(result.message);
      else toast.error(result.message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleGrantAllAccess = async () => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    setGrantingAll(true);

    try {
      const result = await updateAllAccess({
        adminId: selectedAdmin.id,
      });

      if (result.success) {
        toast.success(result.message);
        await fetchAssignedAccessForAdmin(selectedAdmin.id);
      } else {
        toast.error(result.message);
      }
    } finally {
      setGrantingAll(false);
    }
  };

  const handleRefreshAssigned = () => {
    if (!selectedAdmin?.id) {
      toast.error("Please select admin first.");
      return;
    }

    fetchAssignedAccessForAdmin(selectedAdmin.id);
  };


  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    if (
      !newAdmin.admin_username ||
      !newAdmin.first_name ||
      !newAdmin.admin_loginid ||
      !newAdmin.admin_password ||
      !newAdmin.emp_ID ||
      !newAdmin.role
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    setCreatingAdmin(true);

    try {
      const result = await insertAdminLogin(newAdmin);

      if (result.success) {
        toast.success(result.message);

        setShowCreateAdmin(false);

        setNewAdmin({
          admin_username: "",
          first_name: "",
          last_name: "",
          admin_loginid: "",
          admin_password: "",
          emp_ID: "",
          billing_name: "",
          role: "",
        });

        await loadInitial();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Create admin error:", error);
      toast.error("Failed to create admin.");
    } finally {
      setCreatingAdmin(false);
    }
  };

  const getClientId = (client) => {
    return String(
      client?.id ??
      client?.client_id ??
      client?.userid ??
      client?.user_id ??
      client?.pk_client_id ??
      client?.customer_id ??
      ""
    );
  };

  const getClientName = (client) => {
    return (
      client?.client_name ||
      client?.restaurant_name ||
      client?.organization_name ||
      client?.us_name ||
      client?.billing_name ||
      client?.username ||
      client?.userid ||
      client?.user_id ||
      "-"
    );
  };

  const filteredRestaurants = useMemo(() => {
    const clients = Array.isArray(AllClients) ? AllClients : [];

    const q = restaurantSearch.trim().toLowerCase();

    if (!q) return clients;

    return clients.filter((client) => {
      return (
        String(getClientName(client))
          .toLowerCase()
          .includes(q) ||

        String(client?.email || "")
          .toLowerCase()
          .includes(q) ||

        String(client?.userid || client?.user_id || "")
          .toLowerCase()
          .includes(q) ||

        String(client?.us_code || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [AllClients, restaurantSearch]);

  const isRestaurantSelected = (client) => {
    const clientId = getClientId(client);

    return selectedRestaurants.includes(clientId);
  };
  const isRestaurantAlreadyAdded = (client) => {
    const clientId = getClientId(client);

    return originalRestaurants.includes(clientId);
  };

  const handleToggleRestaurant = (client) => {
    const clientId = getClientId(client);

    if (!clientId) return;

    setSelectedRestaurants((prev) => {
      if (prev.includes(clientId)) {
        return prev.filter((id) => id !== clientId);
      }

      return [...prev, clientId];
    });
  };

  const handleToggleAllRestaurants = () => {
    const visibleIds = filteredRestaurants
      .map((client) => getClientId(client))
      .filter(Boolean);

    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedRestaurants.includes(id));

    if (allSelected) {
      setSelectedRestaurants((prev) =>
        prev.filter((id) => !visibleIds.includes(id))
      );
    } else {
      setSelectedRestaurants((prev) => [
        ...new Set([...prev, ...visibleIds]),
      ]);
    }
  };

  const handleResetRestaurants = () => {
    setSelectedRestaurants([]);
  };

  const handleSaveRestaurants = async () => {

    if (selectedRestaurants.length === 0) {
      toast.error("Please select at least one restaurant.");
      return;
    }

    const mappingList = selectedRestaurants.map((restaurantId) => ({
      fk_admin_id: String(selectedAdmin?.id),
      fk_user_id: String(restaurantId),
    }));


    setSavingRestaurants(true);

    try {
      const result = await insertAdminRestaurantMapping({
        mappingList,
      });

      if (result.success) {
        toast.success(
          result.message || "Restaurant mapping saved successfully."
        );

        // Optional: clear selection after successful save
        setSelectedRestaurants([]);
      } else {
        toast.error(result.message || "Failed to save restaurant mapping.");
      }
    } catch (error) {
      console.error("Save restaurant mapping error:", error);
      toast.error("Failed to save restaurant mapping.");
    } finally {
      setSavingRestaurants(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <UserCog className="h-4 w-4" />
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Admin Users
              </h2>
              <p className="text-[11px] text-gray-500">
                Select admin to manage role and access.
              </p>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setShowCreateAdmin(true)}
                className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                Create Admin
              </button>
            </div>
          </div>

          <div className="relative mt-3">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
            <input
              value={adminSearch}
              onChange={(e) => setAdminSearch(e.target.value)}
              placeholder="Search admin..."
              className="h-8 w-full rounded-md border border-gray-200 bg-gray-50 pl-8 pr-3 text-xs outline-none focus:border-emerald-500 focus:bg-white focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="max-h-[750px] overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-xs text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-gray-500">
              No admin found.
            </div>
          ) : (
            filteredAdmins.map((adm) => {
              const active = selectedAdmin?.id === adm.id;

              return (
                <button
                  key={adm.id}
                  type="button"
                  onClick={() => handleSelectAdmin(adm)}
                  className={`w-full border-b border-gray-100 px-4 py-3 text-left transition ${active ? "bg-emerald-50" : "hover:bg-gray-50"
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[13px] font-semibold text-gray-900">
                        {adm.admin_username || "-"}
                      </div>

                      <div className="text-[11px] text-gray-500">
                        {[adm.first_name, adm.last_name]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </div>

                      <div className="mt-1 flex flex-wrap gap-1">
                        {adm.emp_ID && (
                          <span className="rounded-full bg-gray-100 px-2 py-[2px] text-[10px] text-gray-700">
                            EMP: {adm.emp_ID}
                          </span>
                        )}

                        {adm.admin_loginid && (
                          <span className="rounded-full bg-gray-100 px-2 py-[2px] text-[10px] text-gray-700">
                            {adm.admin_loginid}
                          </span>
                        )}
                      </div>
                    </div>

                    {active && (
                      <span className="rounded-full bg-emerald-100 px-2 py-[2px] text-[10px] font-semibold text-emerald-700">
                        Selected
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <ShieldCheck className="h-4 w-4" />
              </div>

              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Role Management
                </h2>
                <p className="text-[11px] text-gray-500">
                  Update admin role and grant full access.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                disabled={!selectedAdmin}
                className="h-8 min-w-[180px] rounded-md border border-gray-200 bg-white px-2 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-100"
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.pk_role_id} value={role.pk_role_id}>
                    {role.role_title}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleUpdateRole}
                disabled={!selectedAdmin || savingRole}
                className="inline-flex h-8 items-center gap-1 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {savingRole ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Update Role
              </button>

              <button
                type="button"
                onClick={handleGrantAllAccess}
                disabled={!selectedAdmin || grantingAll}
                className="inline-flex h-8 items-center gap-1 rounded-md bg-orange-500 px-3 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {grantingAll ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckSquare className="h-3.5 w-3.5" />
                )}
                Give All Access
              </button>

              <button
                type="button"
                onClick={handleRefreshAssigned}
                disabled={!selectedAdmin || assignedLoading}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                {assignedLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Refresh Access
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-3 text-xs text-gray-600">
            <div>
              Selected Admin:{" "}
              <span className="font-semibold text-gray-900">
                {selectedAdmin?.admin_username || "No admin selected"}
              </span>
            </div>

            {assignedLoading && (
              <div className="inline-flex items-center text-[11px] text-gray-500">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Loading given access...
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">

          {/* =========================
      TABS
  ========================== */}
          <div className="border-b border-gray-200 px-4">
            <div className="flex items-center gap-6">

              {/* Access Management Tab */}
              <button
                type="button"
                onClick={() => setActiveTab("access")}
                className={`relative py-3 text-sm font-semibold transition-colors ${activeTab === "access"
                    ? "text-emerald-600"
                    : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Access Management

                {activeTab === "access" && (
                  <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-emerald-600" />
                )}
              </button>

              {/* Restaurant Tab */}
              <button
                type="button"
                onClick={() => setActiveTab("restaurant")}
                className={`relative py-3 text-sm font-semibold transition-colors ${activeTab === "restaurant"
                    ? "text-emerald-600"
                    : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                Restaurant

                {activeTab === "restaurant" && (
                  <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-emerald-600" />
                )}
              </button>

            </div>
          </div>

          {/* =========================================================
      ACCESS MANAGEMENT TAB
  ========================================================== */}
          {activeTab === "access" && (
            <>

              {/* Header */}
              <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">

                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    Access Management
                  </h2>

                  <p className="text-[11px] text-gray-500">
                    Select access changes, then click Save Access to enable or
                    disable.
                  </p>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />

                  <input
                    value={menuSearch}
                    onChange={(e) => setMenuSearch(e.target.value)}
                    placeholder="Search menu..."
                    className="h-8 w-56 rounded-md border border-gray-200 bg-gray-50 pl-8 pr-3 text-xs outline-none focus:border-emerald-500 focus:bg-white focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

              </div>

              {/* =====================================================
          ACCESS SUMMARY
      ====================================================== */}
              {selectedAdmin && (
                <div className="grid gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs md:grid-cols-3">

                  {/* Currently Given */}
                  <div className="rounded-lg bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase text-gray-400">
                      Currently Given
                    </div>

                    <div className="mt-1 text-base font-bold text-gray-900">
                      {originalAccess.length}
                    </div>
                  </div>

                  {/* To Enable */}
                  <div className="rounded-lg bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase text-emerald-500">
                      To Enable
                    </div>

                    <div className="mt-1 text-base font-bold text-emerald-600">
                      {pendingChanges.toEnable.length}
                    </div>
                  </div>

                  {/* To Disable */}
                  <div className="rounded-lg bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase text-red-500">
                      To Disable
                    </div>

                    <div className="mt-1 text-base font-bold text-red-600">
                      {pendingChanges.toDisable.length}
                    </div>
                  </div>

                </div>
              )}

              {/* =====================================================
          MENU / SUBMENU ACCESS
      ====================================================== */}
              <div className="max-h-[500px] overflow-auto p-4">

                {/* No Admin Selected */}
                {!selectedAdmin ? (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-xs text-gray-500">
                    Select an admin user first.
                  </div>

                ) : filteredMenus.length === 0 ? (

                  /* No Menu Found */
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-xs text-gray-500">
                    No menu found.
                  </div>

                ) : (

                  /* Menu List */
                  filteredMenus.map((menu) => {

                    const submenuList = menu.submenus || [];

                    const selectedCount = submenuList.filter((sub) =>
                      isChecked(
                        menu.pk_menu_id,
                        sub.pk_submenu_id
                      )
                    ).length;

                    return (
                      <div
                        key={menu.pk_menu_id}
                        className="mb-3 overflow-hidden rounded-xl border border-gray-200 bg-white"
                      >

                        {/* Menu Header */}
                        <div className="flex items-center justify-between bg-gray-50 px-4 py-2">

                          <label className="flex cursor-pointer items-center gap-2">

                            <input
                              type="checkbox"
                              checked={
                                submenuList.length > 0 &&
                                selectedCount === submenuList.length
                              }
                              onChange={() =>
                                handleToggleWholeMenu(menu)
                              }
                              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />

                            <span className="text-sm font-semibold text-gray-900">
                              {menu.menu_title}
                            </span>

                          </label>

                          <span className="rounded-full bg-emerald-50 px-2 py-[2px] text-[10px] font-semibold text-emerald-700">
                            {selectedCount}/{submenuList.length}
                          </span>

                        </div>

                        {/* Submenus */}
                        <div className="grid gap-2 p-3 md:grid-cols-2 xl:grid-cols-3">

                          {submenuList.map((sub) => {

                            const checked = isChecked(
                              menu.pk_menu_id,
                              sub.pk_submenu_id
                            );

                            const originallyGiven = isOriginal(
                              menu.pk_menu_id,
                              sub.pk_submenu_id
                            );

                            const changed =
                              checked !== originallyGiven;

                            return (
                              <label
                                key={sub.pk_submenu_id}
                                className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-xs transition ${checked
                                    ? "border-emerald-100 bg-emerald-50"
                                    : "border-gray-100 bg-white hover:bg-gray-50"
                                  } ${changed
                                    ? "ring-1 ring-orange-200"
                                    : ""
                                  }`}
                              >

                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    handleToggleSingleAccess(
                                      menu.pk_menu_id,
                                      sub.pk_submenu_id
                                    )
                                  }
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />

                                <div className="min-w-0 flex-1">

                                  {/* Submenu Name */}
                                  <div className="font-semibold text-gray-900">
                                    {sub.submenu_title}
                                  </div>

                                  {/* URL */}
                                  <div className="truncate text-[10px] text-gray-500">
                                    {sub.submenu_url}
                                  </div>

                                  {/* Status */}
                                  <div className="mt-1 flex flex-wrap gap-1">

                                    {originallyGiven && (
                                      <span className="rounded-full bg-emerald-100 px-1.5 py-[1px] text-[9px] font-semibold text-emerald-700">
                                        Given
                                      </span>
                                    )}

                                    {changed && checked && (
                                      <span className="rounded-full bg-blue-100 px-1.5 py-[1px] text-[9px] font-semibold text-blue-700">
                                        Will Enable
                                      </span>
                                    )}

                                    {changed && !checked && (
                                      <span className="rounded-full bg-red-100 px-1.5 py-[1px] text-[9px] font-semibold text-red-700">
                                        Will Disable
                                      </span>
                                    )}

                                  </div>

                                </div>

                              </label>
                            );
                          })}

                        </div>

                      </div>
                    );
                  })
                )}

              </div>

              {/* =====================================================
          ACCESS FOOTER
      ====================================================== */}
              <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3 md:flex-row md:items-center md:justify-between">

                {/* Access Count */}
                <div className="text-xs text-gray-600">

                  <span className="font-semibold text-gray-900">
                    {selectedAccess.length}
                  </span>

                  {" "}selected now ·{" "}

                  <span className="font-semibold text-orange-600">
                    {pendingChanges.total}
                  </span>

                  {" "}pending change(s)

                </div>

                {/* Buttons */}
                <div className="flex items-center gap-2">

                  {/* Reset */}
                  <button
                    type="button"
                    onClick={handleResetChanges}
                    disabled={
                      !selectedAdmin ||
                      savingAccess ||
                      pendingChanges.total === 0
                    }
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </button>

                  {/* Save */}
                  <button
                    type="button"
                    onClick={handleSaveAccess}
                    disabled={
                      !selectedAdmin ||
                      savingAccess ||
                      pendingChanges.total === 0
                    }
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {savingAccess ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}

                    Save Access
                  </button>

                </div>

              </div>

            </>
          )}

          {/* =========================================================
      RESTAURANT TAB
  ========================================================== */}
          {activeTab === "restaurant" && (
            <div className="p-4">

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">

                {/* Restaurant Header */}
                <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">

                  <div className="flex items-center gap-2">

                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                      <Store className="h-4 w-4" />
                    </div>

                    <div>
                      <h2 className="text-sm font-semibold text-gray-900">
                        Restaurant Management
                      </h2>

                      <p className="text-[11px] text-gray-500">
                        Select restaurants to assign to the selected admin.
                      </p>
                    </div>

                  </div>

                  {/* Search */}
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />

                    <input
                      value={restaurantSearch}
                      onChange={(e) => setRestaurantSearch(e.target.value)}
                      placeholder="Search restaurant..."
                      className="h-8 w-56 rounded-md border border-gray-200 bg-gray-50 pl-8 pr-8 text-xs outline-none focus:border-emerald-500 focus:bg-white focus:ring-1 focus:ring-emerald-500"
                    />

                    {restaurantSearch && (
                      <button
                        type="button"
                        onClick={() => setRestaurantSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                </div>

                {/* Selected Admin */}
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">

                  <div className="text-xs text-gray-600">
                    Selected Admin:{" "}
                    <span className="font-semibold text-gray-900">
                      {selectedAdmin?.admin_username || "No admin selected"}
                    </span>
                  </div>

                  <div className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                    {selectedRestaurants.length} Selected
                  </div>

                </div>

                {!selectedAdmin ? (
                  /* No Admin Selected */
                  <div className="p-4">

                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-xs text-gray-500">
                      Select an admin user first.
                    </div>

                  </div>
                ) : (
                  <>

                    {/* Select All Header */}
                    <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2">

                      <label className="flex cursor-pointer items-center gap-2">

                        <input
                          type="checkbox"
                          checked={
                            filteredRestaurants.length > 0 &&
                            filteredRestaurants
                              .map((client) => getClientId(client))
                              .filter(Boolean)
                              .every((id) =>
                                selectedRestaurants.includes(id)
                              )
                          }
                          onChange={handleToggleAllRestaurants}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />

                        <span className="text-sm font-semibold text-gray-900">
                          Select All Restaurants
                        </span>

                      </label>

                      <span className="rounded-full bg-emerald-50 px-2 py-[2px] text-[10px] font-semibold text-emerald-700">
                        {selectedRestaurants.length}/{AllClients?.length || 0}
                      </span>

                    </div>

                    {/* Restaurant List */}
                    <div className="max-h-[450px] overflow-auto p-4">

                      {!Array.isArray(AllClients) || AllClients.length === 0 ? (

                        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-xs text-gray-500">
                          No restaurants found.
                        </div>

                      ) : filteredRestaurants.length === 0 ? (

                        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-xs text-gray-500">
                          No restaurant matches your search.
                        </div>

                      ) : (

                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">

                          {filteredRestaurants.map((client) => {

                            const clientId = getClientId(client);

                            const checked = selectedRestaurants.includes(clientId);
                            const alreadyAdded = originalRestaurants.includes(clientId);

                            return (
                              <label
                                key={clientId}
                                className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-xs transition ${checked
                                    ? "border-emerald-100 bg-emerald-50"
                                    : "border-gray-100 bg-white hover:bg-gray-50"
                                  }`}
                              >

                                {/* Checkbox */}
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    handleToggleRestaurant(client)
                                  }
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />

                                <div className="min-w-0 flex-1">

                                  {/* Restaurant Name */}
                                  <div className="font-semibold text-gray-900">
                                    {getClientName(client)}
                                  </div>

                                  {/* Client ID */}
                                  <div className="mt-0.5 truncate text-[10px] text-gray-500">
                                    ID: {client?.us_code || "—"}
                                  </div>

                                  {/* Email */}
                                  {client?.email && (
                                    <div className="truncate text-[10px] text-gray-500">
                                      {client.email}
                                    </div>
                                  )}

                                  {/* Selected Badge */}
                                  <div className="mt-1 flex flex-wrap gap-1">
  {alreadyAdded && (
    <span className="rounded-full bg-blue-100 px-1.5 py-[1px] text-[9px] font-semibold text-blue-700">
      Already Added
    </span>
  )}

  {!alreadyAdded && checked && (
    <span className="rounded-full bg-emerald-100 px-1.5 py-[1px] text-[9px] font-semibold text-emerald-700">
      Newly Selected
    </span>
  )}

  {alreadyAdded && !checked && (
    <span className="rounded-full bg-red-100 px-1.5 py-[1px] text-[9px] font-semibold text-red-700">
      Will Remove
    </span>
  )}
</div>

                                </div>

                              </label>
                            );
                          })}

                        </div>

                      )}

                    </div>

                    {/* Restaurant Footer */}
                    <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3 md:flex-row md:items-center md:justify-between">

                      <div className="text-xs text-gray-600">

                        <span className="font-semibold text-gray-900">
                          {selectedRestaurants.length}
                        </span>

                        {" "}restaurant(s) selected

                      </div>

                      <div className="flex items-center gap-2">

                        {/* Reset */}
                        <button
                          type="button"
                          onClick={handleResetRestaurants}
                          disabled={selectedRestaurants.length === 0}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Reset
                        </button>

                        {/* Save */}
                        <button
                          type="button"
                          onClick={handleSaveRestaurants}
                          disabled={selectedRestaurants.length === 0}

                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                        >
                          {savingRestaurants ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}

                          {savingRestaurants ? "Saving..." : "Save Restaurants"}
                        </button>

                      </div>

                    </div>

                  </>
                )}

              </div>

            </div>
          )}

        </div>
      </div>
      {showCreateAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Create Admin
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Enter the details to create a new admin account.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCreateAdmin(false)}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateAdmin}>
              <div className="grid gap-4 p-5 md:grid-cols-2">

                {/* Admin Username */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Admin Username <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.admin_username}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        admin_username: e.target.value,
                      })
                    }
                    placeholder="Enter username"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.first_name}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        first_name: e.target.value,
                      })
                    }
                    placeholder="Enter first name"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.last_name}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        last_name: e.target.value,
                      })
                    }
                    placeholder="Enter last name"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Login ID */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Mobile No. <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.admin_loginid}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        admin_loginid: e.target.value,
                      })
                    }
                    placeholder="Enter login ID"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Password */}

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Password <span className="text-red-400">*</span>
                  </label>

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newAdmin.admin_password}
                      onChange={(e) =>
                        setNewAdmin({
                          ...newAdmin,
                          admin_password: e.target.value,
                        })
                      }
                      placeholder="Enter password"
                      className="h-9 w-full rounded-md border border-gray-200 px-3 pr-10 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Employee ID */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Employee ID <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.emp_ID}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        emp_ID: e.target.value,
                      })
                    }
                    placeholder="Enter employee ID"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Billing Name */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Billing Name
                  </label>
                  <input
                    type="text"
                    value={newAdmin.billing_name}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        billing_name: e.target.value,
                      })
                    }
                    placeholder="Enter billing name"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Role <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newAdmin.role}
                    onChange={(e) =>
                      setNewAdmin({
                        ...newAdmin,
                        role: e.target.value,
                      })
                    }
                    placeholder="Enter role"
                    className="h-9 w-full rounded-md border border-gray-200 px-3 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setShowCreateAdmin(false)}
                  className="h-9 rounded-md border border-gray-200 bg-white px-4 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={creatingAdmin}
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-emerald-600 px-4 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {creatingAdmin && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}

                  {creatingAdmin ? "Creating..." : "Create Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccessManagement;